import { useContent } from "@/content/ContentProvider";
import React, { useEffect, useRef, useState } from "react";
import { Pill } from "./Reveal";
import { PillButton } from "./Kit";
import ShaderBackground from "./ShaderBackground";
// Animated number that eases to its target
export function Tween({ value, duration = 900, suffix = "" }) {
  const [v, setV] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = from.current;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const cur = start + (value - start) * (1 - Math.pow(1 - p, 3));
      from.current = cur;
      setV(cur);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return (
    <span className="tabular-nums">
      {Math.round(v)}
      {suffix}
    </span>
  );
}
const PATH = "M0 108 C 240 108, 330 102, 470 72 S 760 12, 1000 12";
// With an outro, the stages use the first part of the scroll and the outro rises over the stage as a last slide.
const STAGES_END = 0.8;
const OUTRO_START = 0.85;
export default function Journey({ outro = null }) {
  const { text: cmsText, JOURNEY, JOURNEY_METRICS } = useContent();
  const wrap = useRef(null);
  const slide = useRef(null);
  const hasOutro = Boolean(outro);
  const order = useRef(0);
  const path = useRef(null);
  const [p, setP] = useState(0);
  const [pts, setPts] = useState({
    len: 0,
    marks: [],
  });
  useEffect(() => {
    const el = path.current;
    if (!el) return;
    const len = el.getTotalLength();
    const marks = [0.02, 0.35, 0.68, 0.985].map((f) => {
      const pt = el.getPointAtLength(len * f);
      return {
        x: pt.x / 10,
        y: (pt.y / 120) * 100,
      };
    });
    setPts({
      len,
      marks,
    });
  }, []);
  useEffect(() => {
    let raf = 0;
    const calc = () => {
      raf = 0;
      const r = wrap.current.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      const raw = Math.min(1, Math.max(0, -r.top / total));
      const pr = hasOutro ? Math.min(1, raw / STAGES_END) : raw;
      if (slide.current) {
        const s = Math.min(
          1,
          Math.max(0, (raw - OUTRO_START) / (1 - OUTRO_START)),
        );
        const eased = 1 - Math.pow(1 - s, 3);
        slide.current.style.transform = `translate3d(0, ${((1 - eased) * 104).toFixed(2)}%, 0)`;
      }
      order.current = pr;
      setP((old) => (Math.abs(old - pr) > 0.001 ? pr : old));
    };
    const on = () => {
      if (!raf) raf = requestAnimationFrame(calc);
    };
    calc();
    window.addEventListener("scroll", on, {
      passive: true,
    });
    window.addEventListener("resize", on);
    return () => {
      window.removeEventListener("scroll", on);
      window.removeEventListener("resize", on);
      cancelAnimationFrame(raf);
    };
  }, [hasOutro]);
  const idx = Math.min(JOURNEY.length - 1, Math.floor(p * JOURNEY.length));
  const st = JOURNEY[idx];
  const frac = 0.02 + p * 0.965;
  const dot =
    pts.marks.length && path.current
      ? path.current.getPointAtLength(pts.len * frac)
      : {
          x: 0,
          y: 108,
        };
  return (
    <section
      id="journey"
      ref={wrap}
      className={`relative scroll-mt-0 ${hasOutro ? "h-[650vh]" : "h-[520vh]"}`}
      data-testid="journey"
    >
      <div className="nm-journey-stage sticky top-0 overflow-hidden bg-[#f7f7f7]">
        <ShaderBackground
          strength={0.4}
          lines={17}
          fadeLeft={0}
          orderRef={order}
        />
        <div className="nm-journey-frame relative z-10 mx-auto flex h-full max-w-[1400px] flex-col px-5 pb-8 pt-24">
          <div className="flex items-center justify-between">
            <Pill white>{cmsText("Journey.Journey.the-journey.7fe9eb")}</Pill>
            <span
              className="rounded-full bg-white px-4 py-1.5 text-[12px] font-medium tabular-nums"
              data-testid="journey-counter"
            >
              {st.n} / 0{JOURNEY.length}
            </span>
          </div>

          <div className="nm-journey-layout grid flex-1 items-center gap-6 lg:grid-cols-12 lg:gap-10">
            <div
              className="nm-journey-type min-w-0 lg:col-span-7"
              aria-live="polite"
            >
              <div
                className="nm-journey-word nm-display flex items-baseline font-medium leading-[0.9] tracking-[-0.03em]"
                data-testid="journey-word"
              >
                <span>{cmsText("Journey.Journey.n.b51a60")}</span>
                <span className="nm-word-mask">
                  <span key={st.key} className="nm-word">
                    {st.rest}
                  </span>
                </span>
              </div>
            </div>

            <div className="nm-journey-detail min-w-0 lg:col-span-5">
              <div
                key={st.key}
                className="nm-journey-card animate-in fade-in slide-in-from-bottom-4 rounded-[24px] bg-white/85 p-5 backdrop-blur-md duration-700 sm:p-7"
              >
                <span className="inline-flex rounded-full bg-black px-3 py-1 text-[11px] font-medium text-white">
                  {st.tag}
                </span>
                <h3 className="nm-title mt-4 text-[28px] leading-[1.08] sm:text-[36px] xl:text-[40px]">
                  {st.head}
                </h3>
                <p className="mt-3 max-w-[480px] text-[14px] leading-[1.4] opacity-70 sm:text-[17px]">
                  {st.text}
                </p>
                {st.service && (
                  <div className="mt-5 hidden sm:block">
                    <PillButton to={st.to} size="lg">
                      {st.service}
                    </PillButton>
                  </div>
                )}
              </div>
              <div
                className="nm-journey-metrics mt-4 space-y-3 rounded-[20px] bg-white/90 p-4 backdrop-blur-sm sm:mt-6 sm:p-5"
                data-testid="journey-metrics"
              >
                {JOURNEY_METRICS.map((label, k) => (
                  <div key={label}>
                    <div className="flex items-center justify-between text-[12px]">
                      <span className="opacity-60">{label}</span>
                      <span className="max-w-[55%] text-right font-medium">
                        {st.status[k]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className="nm-journey-path relative mt-6 h-[64px] w-full shrink-0 sm:h-[96px]"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 1000 120"
              preserveAspectRatio="none"
              className="absolute inset-0 h-full w-full overflow-visible"
            >
              <path
                ref={path}
                d={PATH}
                fill="none"
                stroke="rgba(0,0,0,.16)"
                strokeWidth="1.5"
                strokeDasharray="2 6"
                vectorEffect="non-scaling-stroke"
              />
              <defs>
                <clipPath id="nm-curve-clip">
                  <rect x="0" y="-20" width={Math.max(dot.x, 0)} height="170" />
                </clipPath>
              </defs>
              <path
                d={PATH}
                fill="none"
                stroke="#000"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                clipPath="url(#nm-curve-clip)"
              />
            </svg>
            {pts.marks.map((m, k) => (
              <span
                key={k}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{
                  left: `${m.x}%`,
                  top: `${m.y}%`,
                }}
              >
                <span
                  className={`block h-2.5 w-2.5 rounded-full border border-black transition-colors duration-500 ${k <= idx ? "bg-black" : "bg-[#f7f7f7]"}`}
                />
                <span
                  className={`absolute left-1/2 top-4 -translate-x-1/2 whitespace-nowrap text-[11px] font-medium transition-opacity duration-500 ${k === idx ? "opacity-100" : "opacity-35"} ${k === 0 ? "translate-x-[-10%]" : ""} ${k === 3 ? "translate-x-[-70%]" : ""}`}
                >
                  {JOURNEY[k].key.replace(/^./, (c) => c.toUpperCase())}
                </span>
              </span>
            ))}
            <span
              className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black ring-4 ring-white/80"
              style={{
                left: `${dot.x / 10}%`,
                top: `${(dot.y / 120) * 100}%`,
              }}
            />
          </div>
        </div>
        {hasOutro && (
          <div
            ref={slide}
            className="absolute inset-3 z-20 will-change-transform sm:inset-5"
            style={{ transform: "translate3d(0, 104%, 0)" }}
          >
            {outro}
          </div>
        )}
      </div>
    </section>
  );
}
